import { db } from "@/lib/firebase";
import {
  Offer,
  OfferState,
  safeOfferParse,
} from "@/schemas/firestore/offer.schema";
import { OfferGenerationService } from "@/lib/offerGenerationService";
import { collection, onSnapshot, orderBy, query } from "firebase/firestore";
import { StateCreator } from "zustand";
import { StoreState } from "..";

export interface OfferSlice extends OfferState {
  /* actions */
  subscribeToOffers: (orgId: string, domainId: string) => () => void;
  setSelectedOffer: (offerId: string | null) => void;
  /**
   * Toggle whether an offer feeds generation.
   *
   * The only offer write the client can make. Creation, editing and deletion
   * are backend-only (`allow write: if false` on the collection), so offers
   * are produced solely by the AI generation flow.
   */
  setOfferEnabled: (
    domainId: string,
    offerId: string,
    isEnabled: boolean,
  ) => Promise<void>;
  clearOffers: () => void;
  unsubscribeOffers: () => void;

  /* internals */
  _offerSubscriptions: Map<string, () => void>;
}

export const createOfferSlice: StateCreator<StoreState, [], [], OfferSlice> = (
  set,
  get,
) => ({
  /* ---------- initial state ---------- */
  offers: [],
  selectedOfferId: null,
  isLoading: false,
  error: null,
  _offerSubscriptions: new Map(),

  /* ---------- actions ---------- */
  subscribeToOffers: (orgId: string, domainId: string) => {
    if (!orgId || !domainId) {
      console.log("Offer subscription requires orgId and domainId.");
      return () => {};
    }

    // Check if subscription already exists for this domain
    const existingUnsubscribe = get()._offerSubscriptions.get(domainId);
    if (existingUnsubscribe) {
      console.log(`Offers subscription already exists for domain ${domainId}`);
      return existingUnsubscribe;
    }

    // Only set loading if no offers exist yet
    set((state) => ({
      isLoading: state.offers.length === 0,
      error: null,
    }));

    console.log(`Setting up real-time offers listener for domain ${domainId}`);

    const offersRef = collection(
      db,
      `organizations/${orgId}/domains/${domainId}/offers`,
    );
    const q = query(offersRef, orderBy("created_at", "desc"));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const offersList: Offer[] = [];
        snapshot.docs.forEach((docSnapshot) => {
          const data = docSnapshot.data();
          const offerData = {
            // Spread first, override after — see the note in `ideaSlice`. Zod
            // strips anything outside `OfferSchema`, so nothing extra leaks in.
            ...data,
            id: docSnapshot.id,
            user_id: data.user_id || get().currentUser?.uid || "",
            domain_id: data.domain_id || domainId,
            page_id: data.page_id || get().userData?.currentPageId || "",
            is_enabled: data.is_enabled ?? true,
            offer_context: data.offer_context || null,
            value_messaging: data.value_messaging || null,
            conversion_elements: data.conversion_elements || null,
            custom_note: data.custom_note || "",
            status: data.status || "completed",
            error_message: data.error_message || "",
            created_at: data.created_at?.toDate() || new Date(),
            updated_at: data.updated_at?.toDate() || new Date(),
          };

          const result = safeOfferParse(offerData);
          if (result.success) {
            offersList.push(result.data);
          } else {
            console.error(
              `Validation failed for offer ${docSnapshot.id}:`,
              result.error.issues,
            );
          }
        });

        console.log(
          `Offers snapshot: ${snapshot.docs.length} docs, ${offersList.length} valid for domain ${domainId}`,
        );

        // Merge with offers of other domains
        set((state) => {
          const other = state.offers.filter((o) => o.domain_id !== domainId);
          return { offers: [...other, ...offersList], isLoading: false };
        });

        // Sync header tools with updated offers
        get().resetHeaderTools?.();
      },
      (error) => {
        console.error(
          `Offers subscription error for domain ${domainId}:`,
          error,
        );
        set({
          error: "Failed to subscribe to offers",
          isLoading: false,
        });
      },
    );

    // Wrap unsubscribe to remove from Map when called
    const wrappedUnsubscribe = () => {
      unsubscribe();
      get()._offerSubscriptions.delete(domainId);
      console.log(`Unsubscribed from offers for domain ${domainId}`);
    };

    // Store wrapped unsubscribe in Map keyed by domainId
    set((state) => {
      const newSubscriptions = new Map(state._offerSubscriptions);
      newSubscriptions.set(domainId, wrappedUnsubscribe);
      return { _offerSubscriptions: newSubscriptions };
    });

    return wrappedUnsubscribe;
  },

  setSelectedOffer: (offerId: string | null) => {
    set({ selectedOfferId: offerId });
  },

  setOfferEnabled: async (
    domainId: string,
    offerId: string,
    isEnabled: boolean,
  ) => {
    if (!domainId || !offerId) {
      throw new Error("Domain ID and Offer ID are required to toggle an offer");
    }

    try {
      // Backend-mediated: offers are `allow write: if false` in
      // firestore.rules, so this cannot be a direct Firestore write. The
      // subscription picks up the change and refreshes local state.
      await OfferGenerationService.setOfferEnabled(offerId, domainId, isEnabled);
    } catch (error) {
      console.error("Error toggling offer:", error);
      set({ error: "Failed to update offer" });
      throw error;
    }
  },

  clearOffers: () => {
    set({
      offers: [],
      selectedOfferId: null,
      isLoading: false,
      error: null,
    });
  },

  unsubscribeOffers: () => {
    const subscriptions = get()._offerSubscriptions;
    console.log(`Unsubscribing from ${subscriptions.size} offer subscriptions`);

    // Unsubscribe from all domain listeners
    subscriptions.forEach((unsubscribe, domainId) => {
      console.log(`Unsubscribing from offers for domain ${domainId}`);
      unsubscribe();
    });

    // Clear state
    set({
      offers: [],
      selectedOfferId: null,
      _offerSubscriptions: new Map(),
    });
  },
});
